import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";
import {
  CashBankAccountsResponseDTO,
  CashBankSplitTransactionRequestDTO,
  CashBankSplitTransactionResponseDTO,
  CashBankSummaryQueryParamsDTO,
  CashBankSummaryResponseDTO,
  CashBankTransactionQueryParamsDTO,
  CashBankTransactionsResponseDTO,
} from "../models/CashAndBankDTOs";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

const getAuthHeaders = () => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new APIError(
      data?.message || `HTTP error! status: ${response.status}`,
      response.status,
      data,
    );
  }

  return data as T;
};

const appendTransactionQueryParams = (
  queryParams: URLSearchParams,
  params: CashBankTransactionQueryParamsDTO,
) => {
  queryParams.append("account_id", params.account_id);
  queryParams.append("debit_or_credit", params.debit_or_credit);
  queryParams.append("status", params.status);
  queryParams.append("limit", String(params.limit ?? 10));
  queryParams.append("offset", String(params.offset ?? 0));
  queryParams.append("sort_by", params.sort_by ?? "date");
  queryParams.append("sort_order", params.sort_order ?? "asc");

  if (params.search?.trim()) {
    queryParams.append("search", params.search.trim());
  }

  if (params.date_from) {
    queryParams.append("date_from", params.date_from);
  }

  if (params.date_to) {
    queryParams.append("date_to", params.date_to);
  }
};

export const fetchCashBankAccounts =
  async (): Promise<CashBankAccountsResponseDTO> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cashAndBank/all-accounts`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      return parseResponse<CashBankAccountsResponseDTO>(response);
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );
    }
  };

export const fetchCashBankTransactions = async (
  params: CashBankTransactionQueryParamsDTO,
): Promise<CashBankTransactionsResponseDTO> => {
  try {
    const queryParams = new URLSearchParams();
    appendTransactionQueryParams(queryParams, params);

    const response = await fetch(
      `${API_BASE_URL}/api/cashAndBank/transactions?${queryParams.toString()}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    return parseResponse<CashBankTransactionsResponseDTO>(response);
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const splitCashBankTransaction = async (
  payload: CashBankSplitTransactionRequestDTO,
): Promise<CashBankSplitTransactionResponseDTO> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/bank-transactions/split`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return parseResponse<CashBankSplitTransactionResponseDTO>(response);
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const fetchCashBankSummary = async (
  params: CashBankSummaryQueryParamsDTO = {},
): Promise<CashBankSummaryResponseDTO> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.account_id) {
      queryParams.append("account_id", params.account_id);
    }

    if (params.date_from) {
      queryParams.append("date_from", params.date_from);
    }

    if (params.date_to) {
      queryParams.append("date_to", params.date_to);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/cashAndBank/pending-summary${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    return parseResponse<CashBankSummaryResponseDTO>(response);
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
